import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import {
  UtensilsCrossed,
  Clock,
  Flame,
  CheckCircle2,
  ChevronRight,
  Circle,
} from "lucide-react";
import { useTimeFormatter } from "@/lib/timeFormat";

export default function DailyMealList({
  dayLabel = "Today",
  meals = [],
  completedMealsSet = new Set(),
  onSelectMeal,
  onToggleMealQuick,
}) {
  const fmtTime = useTimeFormatter();
  const totalCount = meals.length;
  const completedCount = meals.filter((m) =>
    completedMealsSet.has(`${m.plan_id || ""}-${m.meal_name}`)
  ).length;

  return (
    <div className="space-y-3" data-testid="daily-meals-section">
      <HealthSourceNote topic="food" />
      {/* Section Header */}
      <div className="flex items-center justify-between px-1">
        <div>
          <h2 className="text-sm font-bold text-white tracking-wide">
            {dayLabel}’s Meals
          </h2>
          <p className="text-[11px] text-slate-400">
            {completedCount} of {totalCount} meals logged
          </p>
        </div>

        {totalCount > 0 && completedCount === totalCount && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold">
            <CheckCircle2 className="w-3 h-3" /> All Done
          </span>
        )}
      </div>

      {/* Meals List */}
      {totalCount === 0 ? (
        <div className="p-6 rounded-2xl bg-[#071326] border border-white/10 text-center space-y-1">
          <UtensilsCrossed className="w-7 h-7 text-slate-500 mx-auto mb-2" />
          <p className="text-xs font-bold text-white">No meals scheduled</p>
          <p className="text-[11px] text-slate-400">
            Assign a meal plan to see your daily food breakdown.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {meals.map((meal, index) => {
            const isCompleted = completedMealsSet.has(
              `${meal.plan_id || ""}-${meal.meal_name}`
            );
            const foods = Array.isArray(meal.foods) ? meal.foods : [];
            const mealCals = foods.reduce(
              (sum, f) => sum + (Number(f.calories) || 0),
              0
            );
            const foodSummary = foods.map((f) => f.name).filter(Boolean).slice(0, 3).join(" · ");

            return (
              <div
                key={`${meal.meal_name}-${index}`}
                onClick={() => onSelectMeal(meal, index)}
                data-testid={`meal-row-${index}`}
                className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between gap-3 shadow-sm cursor-pointer select-none active:scale-[0.99] ${
                  isCompleted
                    ? "bg-[#051122]/90 border-emerald-500/30 hover:border-emerald-500/50"
                    : "bg-[#071326] border-white/10 hover:border-sky-400/40"
                }`}
              >
                {/* Left: Thumbnail & Details */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {/* Thumbnail / Status icon */}
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border overflow-hidden ${
                      isCompleted
                        ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
                        : "bg-white/5 border-white/10 text-slate-400"
                    }`}
                  >
                    {meal.image_url ? (
                      <img
                        src={meal.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <UtensilsCrossed className="w-5 h-5 text-sky-400" />
                    )}
                  </div>

                  {/* Meal info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3
                        className={`text-xs font-bold truncate ${
                          isCompleted ? "text-emerald-300" : "text-white"
                        }`}
                      >
                        {meal.meal_name}
                      </h3>
                      {isCompleted && (
                        <span className="px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-400 text-[9px] font-bold shrink-0">
                          Logged
                        </span>
                      )}
                    </div>

                    {foodSummary && (
                      <p className="text-[11px] text-slate-400 truncate mt-0.5">
                        {foodSummary}
                      </p>
                    )}

                    <div className="flex items-center gap-2.5 text-[10px] text-slate-400 mt-1">
                      {mealCals > 0 && (
                        <span className="flex items-center gap-1 font-semibold text-orange-400">
                          <Flame className="w-3 h-3" />
                          {mealCals} kcal
                        </span>
                      )}
                      {meal.time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {fmtTime(meal.time)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Quick Action & Chevron */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleMealQuick(meal);
                    }}
                    className={`p-1.5 rounded-xl border transition active:scale-90 cursor-pointer ${
                      isCompleted
                        ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                        : "bg-white/5 border-white/10 text-slate-400 hover:text-white"
                    }`}
                    title={isCompleted ? "Mark Unlogged" : "Mark as Logged"}
                    aria-label={isCompleted ? "Mark Unlogged" : "Mark as Logged"}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                  </button>

                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
